import { FINTRAC_STR_DEFINITION_TYPE_CODE } from 'app/modules/fintracStr/formConstants';
import { FintracStrFilingDefinition } from 'app/modules/fintracStr/formModels';
import {
  FintracFormDefaultValue,
  FintracFormFieldConditional,
  FintracFormValue,
  FintracFormValueOrArray,
} from 'app/modules/fintracStr/models';
import {
  U21HelpTooltip,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { compact, isUndefined } from 'lodash';
import pluralize, { singular } from 'pluralize';

export const getUniqueDefinitionOptions = (
  flattenedValues: Record<string, any>,
  usedOptionsPath: string,
  selectedItemPath: string,
): U21SelectOptionProps<string>[] => {
  const usedOptions: ({ typeCode: number; refId: string } | undefined)[] =
    flattenedValues[usedOptionsPath] ?? [];

  // Creating a map of selected type codes from used options
  const selectedCodes = usedOptions.reduce<Record<string, boolean>>(
    (acc, curr) => {
      if (curr?.refId) {
        acc[curr.refId] = true;
      }
      return acc;
    },
    {},
  );

  const selectedHolder = flattenedValues[selectedItemPath];
  const addedOptions: Set<string> = new Set();

  const uniqueOptions: U21SelectOptionProps<string>[] = [];
  const existingOptions: U21SelectOptionProps<string>[] = [];
  (flattenedValues.definitions ?? []).forEach(
    (curr: FintracStrFilingDefinition, index) => {
      const { typeCode, surname, givenName, nameOfEntity } = curr;

      // Filtering options based on selected type code
      if (typeCode === selectedHolder?.typeCode) {
        if (isUndefined(curr.refId)) {
          return;
        }

        const filledName =
          typeCode === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_NAME.value ||
          typeCode === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_DETAILS.value ||
          typeCode ===
            FINTRAC_STR_DEFINITION_TYPE_CODE
              .ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS.value
            ? nameOfEntity?.trim()
            : compact([surname?.trim(), givenName?.trim()]).join(', ');

        const subjectName = filledName || `Subject ${index + 1}`; // We are using surname, given name or both if they are defined, otherwise we are using Subject 1, 2, 3, etc.

        if (selectedCodes[curr.refId]) {
          // If the selected option is part of the valid options, we need to add it to possible options
          existingOptions.push({
            value: curr.refId,
            text: subjectName,
            description: `Reference ID: ${curr.refId}`,
            endIcon: curr.autofilledFields?.[0] ? (
              <U21HelpTooltip help="This subject can autofill fields for transaction beneficiaries." />
            ) : undefined,
          });
        } else {
          // Handles the case where the current option is part of the selected type code but not previously selected
          uniqueOptions.push({
            value: curr.refId,
            text: subjectName,
            description: `Reference ID: ${curr.refId}`,
            endIcon: curr.autofilledFields?.[0] ? (
              <U21HelpTooltip help="This subject can autofill fields for transaction beneficiaries." />
            ) : undefined,
          });
          addedOptions.add(curr.refId);
        }
      }
    },
  );

  return selectedHolder?.refId &&
    !addedOptions.has(selectedHolder?.refId) &&
    existingOptions.length > 0
    ? [...uniqueOptions, ...existingOptions]
    : uniqueOptions;
};

export const getIsAddEntryButtonDisabled = (
  maxRepeat?: number,
  length?: number,
): boolean =>
  !isUndefined(length) && !isUndefined(maxRepeat) && length >= maxRepeat;

export const getAddEntryButtonTooltip = (
  title: string,
  maxRepeat?: number,
  length?: number,
): string | undefined =>
  getIsAddEntryButtonDisabled(maxRepeat, length)
    ? `Maximum ${pluralize(title, maxRepeat, true)} allowed`
    : undefined;

export const flattenValues = (
  obj: Record<string, any | (() => any)>,
  prefix = '',
): Record<string, FintracFormValueOrArray> => {
  const result: Record<string, FintracFormValueOrArray> = {};
  const stack: { obj: FintracFormDefaultValue; prefix: string }[] = [
    { obj, prefix },
  ];

  while (stack.length > 0) {
    const { obj: currentObj, prefix: currentPrefix } = stack.pop()!;

    if (
      isUndefined(currentObj) ||
      currentObj === null ||
      typeof currentObj !== 'object'
    ) {
      // Only add to result if we have a valid prefix
      if (currentPrefix !== '') {
        result[currentPrefix] =
          typeof currentObj === 'function' ? currentObj() : currentObj;
      }
    } else if (Array.isArray(currentObj)) {
      // Handle arrays with bracket notation
      if (currentPrefix !== '') {
        // Store the original array
        result[currentPrefix] = currentObj;
      }

      // Also flatten array items if array is not empty
      if (currentObj.length > 0) {
        currentObj.forEach((item, index) => {
          const newKey = currentPrefix
            ? `${currentPrefix}[${index}]`
            : `[${index}]`;

          // Store the intermediate array item value as well
          if (newKey !== '') {
            result[newKey] = item;
          }

          stack.push({ obj: item, prefix: newKey });
        });
      }
    } else {
      // Handle objects
      const entries = Object.entries(currentObj);

      if (entries.length === 0) {
        // Only add empty objects if we have a valid prefix
        if (currentPrefix !== '') {
          result[currentPrefix] = currentObj;
        }
      } else {
        // Store the intermediate object value as well (if it has a prefix)
        if (currentPrefix !== '') {
          result[currentPrefix] = currentObj;
        }

        for (const [key, value] of entries) {
          const newKey = currentPrefix ? `${currentPrefix}.${key}` : key;
          stack.push({ obj: value, prefix: newKey });
        }
      }
    }
  }

  return result;
};

export const getDependentPaths = (
  conditionals?: FintracFormFieldConditional[],
  absolutePaths?: string[],
  sectionIndex?: number,
  fieldSectionIndex?: number[],
): string[] => {
  const allPaths: string[] = [];

  if (isUndefined(sectionIndex)) {
    return allPaths;
  }

  if (conditionals) {
    conditionals.forEach((c) => {
      if (c.and) {
        c.and.forEach(({ path }) => {
          allPaths.push(
            typeof path === 'function'
              ? path(sectionIndex, fieldSectionIndex)
              : path,
          );
        });
      }

      if (c.or) {
        c.or.forEach(({ path }) => {
          allPaths.push(
            typeof path === 'function'
              ? path(sectionIndex, fieldSectionIndex)
              : path,
          );
        });
      }
    });
  }

  if (absolutePaths) {
    allPaths.push(...absolutePaths);
  }

  return Array.from(new Set(allPaths)); // To deduplicate paths
};

export const asyncDebounce = <TValues, TErrors extends object>(
  asyncValidate: (values: TValues) => Promise<TErrors>,
  defaultErrors: TErrors,
  wait = 300,
) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TValues | null = null;
  let resolvers: Array<(value: TErrors) => void> = [];

  return (values: TValues): Promise<TErrors> => {
    lastArgs = values;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    const promise = new Promise<TErrors>((resolve) => {
      resolvers.push(resolve);
    });

    timer = setTimeout(async () => {
      const args = lastArgs;
      const toResolve = resolvers;
      resolvers = [];
      timer = null;

      if (args == null) {
        toResolve.forEach((r) => r(defaultErrors));
        return;
      }

      try {
        const result = await asyncValidate(args);
        toResolve.forEach((r) => r(result));
      } catch {
        toResolve.forEach((r) => r(defaultErrors));
      }
    }, wait);

    return promise;
  };
};

// Note that we are only grouping based on tab and the highest level section since thats all we need for determining error states
export const groupFormState = <KValue, TValue>(
  object?: Record<string, KValue>,
  valueComparator: (value: KValue) => boolean = (value) => Boolean(value),
  valueOverride: (value: KValue) => TValue = (value) =>
    value as unknown as TValue,
): Record<string, Record<string, TValue>> => {
  if (isUndefined(object)) {
    return {};
  }

  const groupedTouched: Record<string, Record<string, TValue>> = {};

  Object.entries(object).forEach(([key, value]) => {
    if (valueComparator(value)) {
      const tabPathWithIndex = key.split('.')[0];
      const tabPathWithoutIndex = tabPathWithIndex.split('[')[0];

      if (isUndefined(groupedTouched[tabPathWithoutIndex])) {
        groupedTouched[tabPathWithoutIndex] = { [key]: valueOverride(value) };
      } else {
        groupedTouched[tabPathWithoutIndex][key] = valueOverride(value);
      }

      if (tabPathWithIndex !== tabPathWithoutIndex) {
        if (isUndefined(groupedTouched[tabPathWithIndex])) {
          groupedTouched[tabPathWithIndex] = { [key]: valueOverride(value) };
        } else {
          groupedTouched[tabPathWithIndex][key] = valueOverride(value);
        }
      }
    }
  });

  return groupedTouched;
};

export const executeNestedFunctions = (
  obj: FintracFormDefaultValue,
): FintracFormValueOrArray => {
  // Handle functions
  if (typeof obj === 'function') {
    return obj();
  }

  // Handle primitive values or null
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Check for Date objects before general object processing
  if (obj instanceof Date) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(executeNestedFunctions) as FintracFormValue[];
  }

  const result: { [key: string]: FintracFormValue } = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = executeNestedFunctions(value) as FintracFormValue;
  }
  return result;
};

export const getRepeatableLabel = (label: string, index: number) =>
  `${singular(label)} ${index + 1}`;
